
import React, { useState, useEffect, useRef, useCallback, createContext } from 'react'
import { get } from '@/api/lib/amplify-wrapper'
import { signOut, fetchAuthSession } from 'aws-amplify/auth'
import { isAuthError, handleAuthError } from '@/api/lib/error-handler'
import { User, UserRole, AuthContextType } from '../types/auth'
import { Employee } from '../types/workforce'
import { createApiHeaders, createImpersonatedApiHeaders, logApiCall } from './services/api-utils'
import { useAmplify } from '../contexts/amplify-context'
import { secureLogger } from './secure-logger'
import { undoManager } from '@/services/undoManager'
import { useTokenRefresh } from '@/hooks/useTokenRefresh'
import { setDatadogUser, clearDatadogUser } from './monitoring/datadog'

// Create AuthContext directly in this file to avoid module loading order issues
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [originalRole, setOriginalRole] = useState<UserRole | null>(null)
  const { isConfigured: isAmplifyConfigured } = useAmplify()

  // Proactive token refresh - keeps session alive while user is active
  useTokenRefresh({
    isAuthenticated: !!user,
    onRefresh: () => {
      console.log('🔐 [AuthProvider] Token proactively refreshed')
    },
    onSessionExpired: () => {
      console.log('🔐 [AuthProvider] Session expired during proactive refresh')
      // User will be redirected by the token refresh hook
    },
  })

  useEffect(() => {
    // Wait for Amplify to be configured before making API calls
    if (!isAmplifyConfigured) {
      return
    }

    const initializeAuth = async () => {
      try {
        // E2E MODE BYPASS: Skip all auth checks and trust localStorage
        const storedUserProfileE2E = localStorage.getItem('user-profile')
        if (import.meta.env.VITE_E2E_MODE === 'true' && storedUserProfileE2E) {
          console.log('🔐 [AuthProvider] E2E mode - bypassing all auth checks, using cached profile')
          const userProfile = JSON.parse(storedUserProfileE2E)
          setUser(userProfile)
          setIsLoading(false)
          return
        }

        // STEP 1: Validate Cognito session FIRST (source of truth)
        // This catches expired tokens BEFORE we trust localStorage
        let session
        try {
          session = await fetchAuthSession()

          // Check if session has valid tokens
          if (!session.tokens?.accessToken) {
            console.log('🔐 [AuthProvider] No valid Cognito session - clearing cache')
            localStorage.removeItem('user-profile')
            localStorage.removeItem('role-override')
            clearDatadogUser()
            setUser(null)
            setIsLoading(false)
            return
          }

          // Check if access token is expired or about to expire (5 min buffer)
          const accessToken = session.tokens.accessToken
          const exp = accessToken.payload.exp as number
          const now = Math.floor(Date.now() / 1000)
          const bufferSeconds = 300 // 5 minutes

          if (exp && exp < now + bufferSeconds) {
            console.log('🔐 [AuthProvider] Token expired or expiring soon, forcing refresh')
            try {
              session = await fetchAuthSession({ forceRefresh: true })
              if (!session.tokens?.accessToken) {
                throw new Error('Token refresh failed - no tokens returned')
              }
            } catch (refreshError) {
              console.error('🔐 [AuthProvider] Token refresh failed:', refreshError)
              localStorage.removeItem('user-profile')
              localStorage.removeItem('role-override')
              clearDatadogUser()
              setUser(null)
              setIsLoading(false)
              // Redirect to login
              window.location.href = '/login?reason=session_expired'
              return
            }
          }
        } catch (sessionError) {
          console.log('🔐 [AuthProvider] No Cognito session:', sessionError)
          // No valid session - clear local storage and stay in logged-out state
          localStorage.removeItem('user-profile')
          localStorage.removeItem('role-override')
          clearDatadogUser()
          setUser(null)
          setIsLoading(false)
          return
        }

        // STEP 2: Session is valid, now load user profile
        const storedUserProfile = localStorage.getItem('user-profile')

        if (storedUserProfile) {
          const userProfile: User = JSON.parse(storedUserProfile)

          // Check if user object has originalRole (from previous session)
          if (userProfile.originalRole) {
            setOriginalRole(userProfile.originalRole)
          }

          // Check if there's a role override in localStorage
          const storedRoleOverride = localStorage.getItem('role-override')
          if (storedRoleOverride) {
            // Role override exists
            if (!userProfile.originalRole) {
              // First time seeing this override - store current role as original
              userProfile.originalRole = userProfile.role
              setOriginalRole(userProfile.role)
            }
            // Apply the override
            userProfile.role = storedRoleOverride as UserRole
          }

          // Use stored profile temporarily to show UI quickly
          setUser(userProfile)

          // Set Datadog user from cached profile (will be updated with fresh data below)
          setDatadogUser({
            id: userProfile.id,
            email: userProfile.email,
            name: userProfile.name,
            role: userProfile.role,
            status: userProfile.status,
            employeeId: userProfile.employee?.employeeId,
            department: userProfile.employee?.currentEngagement?.departmentName || userProfile.employee?.currentEngagement?.department?.name,
            position: userProfile.employee?.currentEngagement?.roleName || userProfile.employee?.currentEngagement?.role?.name,
          })
        }

        // STEP 3: Fetch fresh profile from backend (validates session + gets latest data)
        // Skip in E2E mode - trust localStorage profile
        if (import.meta.env.VITE_E2E_MODE === 'true' && storedUserProfile) {
          console.log('🔐 [AuthProvider] E2E mode - using cached profile, skipping API validation')
          return
        }

        try {
          const refreshedProfile = await getUserProfile()
          if (refreshedProfile) {
            setUser(refreshedProfile)
            localStorage.setItem('user-profile', JSON.stringify(refreshedProfile))
            // Clear any previous API errors on success
            localStorage.removeItem('auth-api-error')

            // Update Datadog with user info for better tracking
            setDatadogUser({
              id: refreshedProfile.id,
              email: refreshedProfile.email,
              name: refreshedProfile.name,
              role: refreshedProfile.role,
              status: refreshedProfile.status,
              employeeId: refreshedProfile.employee?.employeeId,
              department: refreshedProfile.employee?.currentEngagement?.departmentName || refreshedProfile.employee?.currentEngagement?.department?.name,
              position: refreshedProfile.employee?.currentEngagement?.roleName || refreshedProfile.employee?.currentEngagement?.role?.name,
            })
          }
        } catch (refreshError) {
          // NOTE: This catch block should rarely execute because getUserProfile()
          // returns null instead of throwing for 401/403 errors.
          // This is here for unexpected errors only.

          // Extract error details for diagnostics
          const errorMessage = (refreshError as any)?.message || (refreshError as any)?.toString() || 'Unknown error'
          const status = (refreshError as any)?.response?.statusCode || (refreshError as any)?.status || (refreshError as any)?.statusCode

          // Store error for diagnostic panel
          const errorInfo = {
            message: errorMessage,
            status: status,
            timestamp: new Date().toISOString(),
            isClockSkew: errorMessage.includes('Signature not yet current') || errorMessage.includes('Signature expired')
          }
          localStorage.setItem('auth-api-error', JSON.stringify(errorInfo))
          console.error('🔐 [AuthProvider] Unexpected API error:', errorInfo)

          // For 401/403 errors, keep user logged in with cached profile
          // They will be redirected to /pending-access by AuthContainer
          if (status === 401 || status === 403) {
            console.warn(`🔐 [AuthProvider] Unexpected auth error (${status}), keeping cached profile`)
            // Keep using cached profile if available
            if (storedUserProfile) {
              console.log('🔐 [AuthProvider] Using cached profile')
            }
            return
          }

          // For non-auth errors (network issues, etc.), keep cached version
          console.warn('🔐 [AuthProvider] Non-auth error refreshing profile, using cache:', refreshError)
        }
      } catch (error) {
        secureLogger.error('Failed to restore user profile session', { error: error instanceof Error ? error.message : 'Unknown error' })
        // Clear invalid session data
        localStorage.removeItem('user-profile')
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [isAmplifyConfigured])

  // Get user profile from backend API using AWS Amplify
  // This is the SINGLE SOURCE OF TRUTH for user data
  // Fetches from /users/me AND /workforce/employees/me, then merges
  const getUserProfile = async (): Promise<User | null> => {
    try {
      logApiCall('/users/me')

      // Check the exact config structure
      const { Amplify } = await import('aws-amplify')
      const config = Amplify.getConfig()

      // Fetch user from /users/me (generic user module)
      const { response: userResponse } = await get({
        apiName: 'users',
        path: '/users/me',
        options: {
          headers: createApiHeaders()
        }
      })

      const userResult = await userResponse
      const apiData = await userResult.body.json() as Record<string, unknown>
    // Parse basic user data
    const userData: User = {
      id: apiData.id as string || apiData.sub as string || apiData.userId as string || apiData.email as string,
      email: apiData.email as string,
      role: apiData.roleName as UserRole,
      name: apiData.name as string || apiData.email as string,
      status: apiData.status as 'active' | 'inactive' | 'pending' | 'onboarding' || 'active',
      avatar: apiData.avatar as string | undefined,
      employee: undefined, // Will be populated below
      intercomJWT: apiData.intercomJWT as string | undefined
    }
    // Try to fetch employee data (not all users are employees)
    try {
      logApiCall('/workforce/employees/me')

      const { response: employeeResponse } = await get({
        apiName: 'workforce',
        path: '/workforce/employees/me',
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const employeeResult = await employeeResponse
      const employeeData = await employeeResult.body.json()

      // Validate and attach employee data to user
      if (employeeData && typeof employeeData === 'object' && 'employeeId' in employeeData) {
        userData.employee = employeeData as unknown as Employee
      }
    } catch (employeeError) {
      // Check if this is a permission error
      const empStatus = (employeeError as any)?.response?.statusCode || (employeeError as any)?.status || (employeeError as any)?.statusCode
      if (empStatus === 403) {
        console.warn('🔐 [AuthProvider] User lacks permission for /workforce/employees/me')
      }
      // User is not an employee or lacks permission (admin, contractor, etc.)
      userData.employee = undefined
    }

    return userData

    } catch (error) {
      // Check if this is an auth error (401) or permission error (403)
      const status = (error as any)?.response?.statusCode || (error as any)?.status || (error as any)?.statusCode
      if (status === 401 || status === 403) {
        console.warn(`🔐 [AuthProvider] /users/me returned ${status} - user will be redirected to pending-access`)

        // Store error details for diagnostic panel on /pending-access
        const errorMessage = (error as any)?.message || (error as any)?.toString() || 'Unknown error'
        const errorInfo = {
          message: errorMessage,
          status: status,
          timestamp: new Date().toISOString(),
          isClockSkew: errorMessage.includes('Signature not yet current') || errorMessage.includes('Signature expired')
        }
        localStorage.setItem('auth-api-error', JSON.stringify(errorInfo))

        // Return null to let caller handle it - AuthContainer will redirect to /pending-access
        return null
      }

      secureLogger.error('Failed to get user profile from /users/me', { error: error instanceof Error ? error.message : 'Unknown error' })
      return null
    }
  }

  const logout = async () => {
    try {
      // First, sign out from AWS Cognito
      await signOut()

      // Clear undo manager stack on logout
      if (undoManager.isInitialized()) {
        undoManager.reset()
      }

      // Then clear local state and storage
      setUser(null)
      localStorage.removeItem('user-profile')
      localStorage.removeItem('tenant-override') // Clean up tenant data

      // Clear Datadog user tracking
      clearDatadogUser()

      // Redirect to login
      window.location.href = '/login'

    } catch (error) {
      secureLogger.error('Error during logout', { error: error instanceof Error ? error.message : 'Unknown error' })

      // Clear undo manager even on error
      if (undoManager.isInitialized()) {
        undoManager.reset()
      }

      // Even if AWS signOut fails, clean up local data and redirect
      setUser(null)
      localStorage.removeItem('user-profile')
      localStorage.removeItem('tenant-override')
      window.location.href = '/login'
    }
  }

  // Refresh user profile from API
  const refreshUserProfile = async (): Promise<User | null> => {
    const profile = await getUserProfile()
    if (profile) {
      setUser(profile)
      localStorage.setItem('user-profile', JSON.stringify(profile))
    }
    return profile
  }

  // Immediate sync with localStorage (no delays, no clearing)
  const syncWithStorage = () => {
    try {
      const storedUserProfile = localStorage.getItem('user-profile')
      if (storedUserProfile) {
        const userProfile: User = JSON.parse(storedUserProfile)
        setUser(userProfile)
        return true
      } else {
        setUser(null)
        return false
      }
    } catch (error) {
      secureLogger.error('Error syncing with storage', { error: error instanceof Error ? error.message : 'Unknown error' })
      setUser(null)
      return false
    }
  }

  // Force refresh auth state (useful after authentication issues)
  const refreshAuth = async () => {
    setIsLoading(true)
    try {

      // Don't clear localStorage - that defeats the purpose!
      // Just check current state multiple times to handle race conditions
      let attempts = 0
      const maxAttempts = 5
      let userProfile: User | null = null

      while (attempts < maxAttempts && !userProfile) {
        attempts++

        // Check for user profile
        const storedUserProfile = localStorage.getItem('user-profile')
        if (storedUserProfile) {
          try {
            userProfile = JSON.parse(storedUserProfile)
            break
          } catch (parseError) {
            secureLogger.warn('Failed to parse user profile, removing', { error: parseError instanceof Error ? parseError.message : 'Unknown parse error' })
            localStorage.removeItem('user-profile')
          }
        }

        // Wait between attempts, with increasing delay
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, attempts * 100))
        }
      }

      if (userProfile) {
        setUser(userProfile)
      } else {
        setUser(null)
      }
    } catch (error) {
      secureLogger.error('Error during auth refresh', { error: error instanceof Error ? error.message : 'Unknown error' })
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Override user role (admin only feature for testing)
  const overrideUserRole = (role: UserRole) => {
    if (!user) return
    if (user.role !== 'admin' && !user.originalRole) {
      secureLogger.error('Only admins can override roles')
      return
    }

    // Store original role if first override
    if (!user.originalRole) {
      setOriginalRole(user.role)
    }

    // Update user with new role and originalRole
    const updatedUser = {
      ...user,
      role,
      originalRole: user.originalRole || user.role
    }
    setUser(updatedUser)

    // Update localStorage
    localStorage.setItem('user-profile', JSON.stringify(updatedUser))
    localStorage.setItem('role-override', role)

  }

  // Clear role override
  const clearRoleOverride = () => {
    if (!user || !user.originalRole) return

    // Restore original role
    const updatedUser = {
      ...user,
      role: user.originalRole,
      originalRole: undefined
    }
    setUser(updatedUser)

    // Update localStorage
    localStorage.setItem('user-profile', JSON.stringify(updatedUser))
    localStorage.removeItem('role-override')
    setOriginalRole(null)

  }

  return (
    <AuthContext.Provider value={{
      user,
      logout,
      isLoading,
      refreshUser: refreshUserProfile,
      refreshAuth,
      syncWithStorage,
      overrideUserRole,
      clearRoleOverride,
      originalRole
    }}>
      {children}
    </AuthContext.Provider>
  )
}
